import { READINESS_READY_TITLE, READINESS_TITLE } from 'modules/overlay-v2/domain/constants/readiness';
import { OverlayReadinessState } from 'modules/overlay-v2/domain/interfaces/readiness';
import React, { CSSProperties } from 'react';
import { theme } from 'styles/theme/theme';
import UDText from 'ud-ui/components/text';

import ReadinessStepRow from './readiness-step-row';
import { Backdrop, Card, ProgressFill, ProgressTrack, StepList } from './styles';

interface ReadinessChecklistProps {
  readiness: OverlayReadinessState;
  onRetry: () => void;
  className?: string;
  style?: CSSProperties;
}

/**
 * Экран подготовки трансляции: пошаговый чеклист вместо «Загрузка, подождите».
 *
 * Оператор смотрит его в OBS или в отдельном окне и должен без посторонней помощи понять,
 * чего именно не хватает — поэтому шаги названы по-человечески, а у проблемных есть
 * подсказка, что сделать.
 *
 * Когда всё сошлось, карточка не пропадает рывком: сначала показывает «Всё готово»,
 * потом уходит анимацией. Задержку держит `READINESS_EXIT_DURATION_MS` — столько же
 * `useOverlayReadiness` не снимает экран с монтирования.
 */
function ReadinessChecklist({ readiness, onRetry, className, style }: ReadinessChecklistProps) {
  const { steps, subtitle, doneCount, totalCount, isReady, failedStepId } = readiness;
  const percent = totalCount === 0 ? 0 : Math.round((doneCount / totalCount) * 100);

  return (
    <Backdrop isClosing={isReady} className={className} style={style}>
      <Card isClosing={isReady} aria-live={'polite'} aria-busy={!isReady}>
        <UDText as={'h2'} variant={'title-3'} color={theme.colors.SurfaceWhite}>
          {isReady ? READINESS_READY_TITLE : READINESS_TITLE}
        </UDText>

        {subtitle && (
          <UDText variant={'subtitle-2'} color={theme.colors.WhiteGray} style={{ marginTop: 6 }}>
            {subtitle}
          </UDText>
        )}

        <UDText variant={'subtitle-1'} color={theme.colors.WhiteGray} style={{ marginTop: 14 }}>
          {`${doneCount} из ${totalCount}`}
        </UDText>

        <ProgressTrack>
          <ProgressFill percent={percent} hasFailed={Boolean(failedStepId)} />
        </ProgressTrack>

        <StepList>
          {steps.map((step, index) => (
            <ReadinessStepRow key={step.id} step={step} index={index} onRetry={onRetry} />
          ))}
        </StepList>
      </Card>
    </Backdrop>
  );
}

export default React.memo(ReadinessChecklist);
